// Modèle de la page de la landing (liste ordonnée de rubriques) : fonctions pures, jamais de mutation.
// Le même document JSON est validé côté serveur (ourco-server/services/landingPageService.js).

import SECTION_FIELDS from "./sectionFields.json";
import BLOCK_FIELDS from "./blockFields.json";

const DEFAULT_SECTION_TYPES = ["hero", "video", "services", "signup", "testimonials", "threads", "wellbeing", "mood", "syndicat"];

export const DEFAULT_PAGE = {
    schema: 1,
    sections: DEFAULT_SECTION_TYPES.map(type => ({
        id: type, type, visible: true, featured: false, starts_at: null, ends_at: null, data: {},
    })),
};

// Propriétés propres uniquement : « constructor » ou « __proto__ » ne sont pas des types de rubrique.
export const hasOwn = (obj, key) => typeof key === "string" && Object.prototype.hasOwnProperty.call(obj, key);

const indexOfSection = (page, id) => page.sections.findIndex(s => s.id === id);

export function moveSection(page, id, toIndex) {
    const from = indexOfSection(page, id);
    if (from === -1) return page;
    const to = Math.max(0, Math.min(page.sections.length - 1, toIndex));
    if (to === from) return page;
    const sections = [...page.sections];
    const [moved] = sections.splice(from, 1);
    sections.splice(to, 0, moved);
    return {...page, sections};
}

export function moveSectionBy(page, id, delta) {
    const from = indexOfSection(page, id);
    return from === -1 ? page : moveSection(page, id, from + delta);
}

export function updateSection(page, id, patch) {
    if (indexOfSection(page, id) === -1) return page;
    return {...page, sections: page.sections.map(s => (s.id === id ? {...s, ...patch} : s))};
}

export const setVisible = (page, id, visible) => updateSection(page, id, {visible});

// Fond d'une rubrique, quel que soit son type : une rubrique libre le garde dans `settings`, une rubrique intégrée dans
// `background` (absent = fond d'origine). `backgroundId` vide retire le fond d'une rubrique intégrée ; une rubrique libre
// en a toujours un (identifiant inconnu ou vide : page inchangée).
export function setBackground(page, id, backgroundId) {
    const section = page.sections.find(s => s.id === id);
    if (!section) return page;
    if (section.type === "custom") {
        return BLOCK_FIELDS.backgroundIds.includes(backgroundId) ? updateSettings(page, id, {background: backgroundId}) : page;
    }
    if (!backgroundId) {
        const {background, ...rest} = section;
        return {...page, sections: page.sections.map(s => (s.id === id ? rest : s))};
    }
    return BLOCK_FIELDS.backgroundIds.includes(backgroundId) ? updateSection(page, id, {background: backgroundId}) : page;
}

// Fond affiché par le sélecteur de l'admin : "" = fond d'origine (rubriques intégrées seulement).
export const backgroundIdOf = (section) => {
    const id = section.type === "custom" ? (section.settings || {}).background : section.background;
    return BLOCK_FIELDS.backgroundIds.includes(id) ? id : (section.type === "custom" ? BLOCK_FIELDS.backgroundIds[0] : "");
};

// « À la une » : une seule rubrique à la fois, placée juste après l'accroche (en tête si l'accroche n'est pas première).
export function featureSection(page, id) {
    const target = page.sections.find(s => s.id === id);
    if (!target || target.type === "hero") return page;
    const unflagged = page.sections.map(s => (s.featured ? {...s, featured: false} : s));
    const others = unflagged.filter(s => s.id !== id);
    const flagged = {...target, featured: true};
    const insertAt = page.sections[0].type === "hero" ? 1 : 0;
    const sections = [...others.slice(0, insertAt), flagged, ...others.slice(insertAt)];
    return {...page, sections};
}

// Page chargée par l'admin : retire les rubriques de type inconnu et les champs absents du registre (un type ou un
// champ supprimé du code ne doit jamais bloquer l'édition). `removed` = nombre d'éléments retirés ; la page d'origine est
// renvoyée telle quelle (même référence) quand rien n'a été retiré.
export function sanitizePage(page) {
    if (!page || !Array.isArray(page.sections)) return {page: DEFAULT_PAGE, removed: 0};
    let removed = 0;

    function cleanData(data, fields) {
        if (!data || typeof data !== "object") return data;
        let changed = false;
        const out = {};
        for (const [key, value] of Object.entries(data)) {
            const spec = fields[key];
            if (!spec) {
                removed += 1;
                changed = true;
            } else if (spec.kind === "list" && Array.isArray(value)) {
                const items = value.map(item => cleanData(item, spec.itemFields));
                if (items.some((item, i) => item !== value[i])) changed = true;
                out[key] = items;
            } else {
                out[key] = value;
            }
        }
        return changed ? out : data;
    }

    let changed = false;
    const sections = [];
    for (const s of page.sections) {
        if (!s || !hasOwn(SECTION_FIELDS, s.type)) {
            removed += 1;
            changed = true;
            continue;
        }
        let next = s;
        // Un fond inconnu (identifiant retiré du code) ferait refuser l'enregistrement : on le retire.
        if (s.type !== "custom" && s.background !== undefined && !BLOCK_FIELDS.backgroundIds.includes(s.background)) {
            const {background, ...rest} = next;
            next = rest;
            removed += 1;
        }
        const data = cleanData(s.data, SECTION_FIELDS[s.type].fields);
        if (data !== s.data) next = {...next, data};
        if (s.type === "custom") {
            const settings = cleanData(s.settings, BLOCK_FIELDS.settings);
            if (settings !== s.settings) next = {...next, settings};
            if (Array.isArray(s.blocks)) {
                const kept = s.blocks.filter(b => b && BLOCK_FIELDS.blocks[b.type]);
                removed += s.blocks.length - kept.length;
                const blocks = kept.map(b => {
                    const blockData = cleanData(b.data, BLOCK_FIELDS.blocks[b.type].fields);
                    return blockData === b.data ? b : {...b, data: blockData};
                });
                if (kept.length !== s.blocks.length || blocks.some((b, i) => b !== kept[i])) next = {...next, blocks};
            }
        }
        if (next !== s) changed = true;
        sections.push(next);
    }
    return changed ? {page: {...page, sections}, removed} : {page, removed: 0};
}

// "Donnez votre avis, {pas} seulement" -> morceaux dont certains sont mis en valeur (accent).
// Une accolade non fermée ou vide reste du texte brut.
export function parseTitle(title) {
    if (!title) return [];
    const parts = [];
    let last = 0;
    for (const m of title.matchAll(/\{([^{}]+)\}/g)) {
        if (m.index > last) parts.push({text: title.slice(last, m.index), accent: false});
        parts.push({text: m[1], accent: true});
        last = m.index + m[0].length;
    }
    if (last < title.length) parts.push({text: title.slice(last), accent: false});
    return parts;
}

// --- Rubriques libres (type « custom ») : réglages et blocs ---

export const CUSTOM_SETTINGS_DEFAULTS = {name: "Nouvelle rubrique", background: "cream", columns: "1", align: "left", animation: "fade"};

const smallestFreeId = (prefix, taken) => {
    let n = 1;
    while (taken.has(`${prefix}${n}`)) n += 1;
    return `${prefix}${n}`;
};

const isCustom = (page, id) => page.sections.some(s => s.id === id && s.type === "custom");

// Applique `change` à la rubrique libre `id` ; page inchangée (même référence) si elle n'existe pas ou n'est pas libre.
const withCustom = (page, id, change) => (isCustom(page, id)
    ? {...page, sections: page.sections.map(s => (s.id === id ? change(s) : s))}
    : page);

// Ajoute une rubrique libre vide en fin de page. `id` vaut null (page inchangée) à 12 rubriques libres.
export function addCustomSection(page) {
    if (page.sections.filter(s => s.type === "custom").length >= BLOCK_FIELDS.maxCustomSections) return {page, id: null};
    const id = smallestFreeId("rubrique-", new Set(page.sections.map(s => s.id)));
    const section = {
        id, type: "custom", visible: true, featured: false, starts_at: null, ends_at: null, data: {},
        settings: {...CUSTOM_SETTINGS_DEFAULTS}, blocks: [],
    };
    return {page: {...page, sections: [...page.sections, section]}, id};
}

// Seules les rubriques libres peuvent être supprimées (les 9 rubriques intégrées se masquent).
export function removeSection(page, id) {
    return isCustom(page, id) ? {...page, sections: page.sections.filter(s => s.id !== id)} : page;
}

export const updateSettings = (page, id, patch) => withCustom(page, id, s => ({...s, settings: {...(s.settings || {}), ...patch}}));

// Ajoute un bloc d'un type connu ; `blockId` vaut null (page inchangée) si le type est inconnu, si la rubrique n'est pas
// libre ou à 30 blocs.
export function addBlock(page, sectionId, type) {
    const section = page.sections.find(s => s.id === sectionId && s.type === "custom");
    if (!section || !BLOCK_FIELDS.blocks[type]) return {page, blockId: null};
    const blocks = section.blocks || [];
    if (blocks.length >= BLOCK_FIELDS.maxBlocksPerSection) return {page, blockId: null};
    const blockId = smallestFreeId("b-", new Set(blocks.map(b => b.id)));
    return {page: withCustom(page, sectionId, s => ({...s, blocks: [...blocks, {id: blockId, type, data: {}}]})), blockId};
}

export function moveBlock(page, sectionId, blockId, delta) {
    return withCustom(page, sectionId, s => {
        const blocks = [...(s.blocks || [])];
        const from = blocks.findIndex(b => b.id === blockId);
        const to = from + delta;
        if (from === -1 || to < 0 || to >= blocks.length) return s;
        const [moved] = blocks.splice(from, 1);
        blocks.splice(to, 0, moved);
        return {...s, blocks};
    });
}

export function removeBlock(page, sectionId, blockId) {
    const section = page.sections.find(s => s.id === sectionId && s.type === "custom");
    if (!section || !(section.blocks || []).some(b => b.id === blockId)) return page;
    return withCustom(page, sectionId, s => ({...s, blocks: s.blocks.filter(b => b.id !== blockId)}));
}

export const updateBlock = (page, sectionId, blockId, data) =>
    withCustom(page, sectionId, s => ({...s, blocks: (s.blocks || []).map(b => (b.id === blockId ? {...b, data} : b))}));
